import { Button, DialogDisclosure, Heading } from "@ariakit/react";
import {
  DialogTrigger,
  IndieTabletopClubLogo,
  useAppActions,
  useCurrentUser,
  useSessionInfo,
} from "@indietabletop/appkit";
import { toast } from "react-hot-toast";
import { Link } from "wouter";
import { button } from "../../common/common.css.ts";
import { database } from "../../database.ts";
import { m } from "../../messages.ts";
import { useIsAuthenticated } from "../../store.ts";
import { useCheckAccessResult } from "../CheckAccess/context.ts";
import { ConfirmDialog } from "../ConfirmDialog/ConfirmDialog.tsx";
import { conjunction, FeatureEnum } from "../fmt.ts";
import { Page } from "../Page/Page.tsx";
import { page, section } from "./style.css.ts";
import { SyncLogSection } from "./SyncLog.tsx";

function ResetOnboardingDialog() {
  return (
    <ConfirmDialog
      heading="Reset all notes?"
      description="All orientation guide notes will be displayed again."
      submitLabel="Reset"
      onSubmit={() => database.settings.removeOnboardingSettings()}
      onSuccess={() => toast("Onboarding reset!")}
    />
  );
}

function ClearAllDataDialog() {
  return (
    <ConfirmDialog
      heading="Delete all data?"
      description="Are you sure you want to delete all locally stored data? There is no way to undo this."
      submitLabel="Delete all"
      theme="red"
      onSubmit={() => database.clearAll()}
      onSuccess={() => toast("Local data cleared!")}
    />
  );
}

export function SettingsPage() {
  return (
    <Page backHref="~/" title="Settings">
      <div className={page.sections}>
        <AccountSection />
        <SyncLogSection />
        <DangerZoneSection />
      </div>
    </Page>
  );
}

function AccountSection() {
  const { logout } = useAppActions();
  const currentUser = useCurrentUser();
  const sessionInfo = useSessionInfo();
  const [unlocksResult] = useCheckAccessResult();

  return (
    <section className={section.container({ jagged: 1 })}>
      <Heading className={section.heading}>Account</Heading>

      {currentUser ? (
        <>
          <div className={section.info}>
            <p>Logged in as: {currentUser.email}</p>

            {sessionInfo && (
              <>
                <p>
                  Session started:{" "}
                  {new Date(sessionInfo.createdTs).toLocaleString()}
                </p>
                <p>
                  Expires: {new Date(sessionInfo.expiresTs).toLocaleString()}
                </p>
              </>
            )}
            <p>
              Status:{" "}
              {currentUser.isVerified ? "Verified" : "Pending verification"}
            </p>

            <p>
              Unlocked:{" "}
              {unlocksResult.unpack(
                ({ unlocks }) => {
                  return unlocks.length > 0
                    ? conjunction(
                        unlocks
                          .map((unlock) => unlock.feature)
                          .filter((feature) => FeatureEnum.has(feature))
                          .map((feature) => FeatureEnum.get(feature)),
                      )
                    : "None";
                },
                () => "Failed to check unlocks",
                () => "Checking unlocks",
              )}
            </p>

            <div className={section.itcPowered}>
              <div>Powered by</div>
              <IndieTabletopClubLogo className={section.itcLogo} />
            </div>
          </div>

          <div className={section.actions}>
            <Button className={section.logOutButton} onClick={() => logout()}>
              Log out
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className={section.info}>{m.accountJoinOrLoginNudge}</p>

          <div className={section.itcPowered}>
            <div>Powered by</div>
            <IndieTabletopClubLogo className={section.itcLogo} />
          </div>

          <div className={section.actions}>
            <Link className={section.joinButton} href="~/join">
              Join
            </Link>
            <Link className={section.loginButton} href="~/login">
              Log in
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

function DangerZoneSection() {
  const isAuthenticated = useIsAuthenticated();
  const currentUser = useCurrentUser();

  // This section is only available for users that are logged out. For logged
  // in users it would make a mess in syncing.
  if (currentUser || isAuthenticated) {
    return null;
  }

  return (
    <section className={section.container({ warning: true, jagged: 2 })}>
      <Heading className={section.heading}>Danger Zone</Heading>

      <div style={{ display: "grid", gap: "3rem" }}>
        <div>
          <p>Reset all orientation guide notes.</p>

          <div className={section.actions}>
            <DialogTrigger>
              <ResetOnboardingDialog />
              <DialogDisclosure
                className={button({ size: "small", mask: 1, theme: "beige" })}
              >
                Reset all
              </DialogDisclosure>
            </DialogTrigger>
          </div>
        </div>
        <div>
          <p>Delete all locally stored data.</p>

          <div className={section.actions}>
            <DialogTrigger>
              <ClearAllDataDialog />
              <DialogDisclosure className={section.dangerButton}>
                Delete all
              </DialogDisclosure>
            </DialogTrigger>
          </div>
        </div>
      </div>
    </section>
  );
}
